import React from 'react';
import PropTypes from 'prop-types';

import forwardRefWithTranslation from '@/common/forwardRefWithTranslation';
import { getE164PhoneNumberFromCountryAndNationalPhoneNumber } from '@/common/helpers/phone_number';

import {
    EmailInput,
    NameInput,
    RegisterPasswordInput,
    TermsAndConditionsInput,
    SubscriberInput,
    LoyaltyOptIn,
} from './inputs';
import {
    PhoneNumberInput,
    PhoneNumberInputWithValidation,
} from '../common/inputs/phone-number';
import { RegisterButton } from './buttons';

import CustomerVerificationModal from '@/desktop/customer.verification/CustomerVerificationModal';

const {
    UI,
    Customer,
    headoffice: {
        customer_phone_number_validation_enabled,
        customer_phone_number_sms_verification_enabled,
        loyalty_opt_in_enabled,
    },
} = Mobi2Go;

class SignUpForm extends React.Component {
    static propTypes = {
        t: PropTypes.func.isRequired,
        email: PropTypes.string.isRequired,
        onEmailChange: PropTypes.func.isRequired,
        name: PropTypes.string.isRequired,
        onNameChange: PropTypes.func.isRequired,
        phone_number: PropTypes.string.isRequired,
        onPhoneNumberChange: PropTypes.func.isRequired,
        phone_number_country: PropTypes.string.isRequired,
        onPhoneNumberCountryChange: PropTypes.func.isRequired,
        password: PropTypes.string.isRequired,
        onPasswordChange: PropTypes.func.isRequired,
        terms_and_conditions: PropTypes.bool.isRequired,
        onTermsAndConditionsChange: PropTypes.func.isRequired,
        subscriber: PropTypes.bool.isRequired,
        onSubscriberChange: PropTypes.func.isRequired,
        loyaltyOptInValue: PropTypes.bool.isRequired,
        onLoyaltyOptInChange: PropTypes.func.isRequired,
        setSectionState: PropTypes.func.isRequired,
        accountCreatedAlert: PropTypes.func.isRequired,
        loyalty_opt_in_enabled: PropTypes.bool.isRequired,
    };

    customerVerificationModalRef;

    signUp = () => {
        const { terms_and_conditions } = this.props;
        if (!terms_and_conditions) {
            this.termsAndConditionsAlert();
            return;
        }

        const {
            email,
            name,
            phone_number,
            phone_number_country,
            password,
            subscriber,
            loyaltyOptInValue,
            setSectionState,
            accountCreatedAlert,
        } = this.props;

        let phone_number_validated = phone_number;

        if (customer_phone_number_validation_enabled) {
            try {
                phone_number_validated = getE164PhoneNumberFromCountryAndNationalPhoneNumber(
                    phone_number_country,
                    phone_number
                );
            } catch (e) {
                // Default to raw number
                // It will fail validation later
            }
        }

        const customer_data = {
            email,
            name,
            phone_number: phone_number_validated,
            password,
            is_subscriber: subscriber,
            loyalty_opt_in: loyalty_opt_in_enabled ? loyaltyOptInValue : false,
        };

        setSectionState('', 'spinner');
        try {
            Customer.create(customer_data, () => {
                const completeSignUp = () => {
                    if (!Customer.isGuest()) {
                        accountCreatedAlert();
                    }
                };

                if (customer_phone_number_sms_verification_enabled) {
                    this.verifyPhoneNumber(() => {
                        completeSignUp();
                    });
                } else {
                    completeSignUp();
                }
            });
        } catch (error) {
            setSectionState('', '');

            if (
                customer_data.name.length === 0 ||
                error.message === 'name required to create customer'
            ) {
                this.nameRequiredAlert();
            } else if (customer_data.phone_number.length === 0) {
                this.phoneNumberRequiredAlert();
            } else if (
                error.message ===
                'valid phone number required to create customer'
            ) {
                this.phoneNumberInvalidAlert();
            } else if (customer_data.password.length === 0) {
                this.passwordRequiredAlert();
            } else if (
                error.message === 'valid password required to create customer'
            ) {
                this.passwordInvalidAlert();
            }
        }
    };

    nameRequiredAlert = () => {
        const { t } = this.props;
        UI.alert({
            id: 'name-required',
            title: t('register_enter_name_title'),
            body: t('register_enter_name_body'),
            temp: true,
            primary_button: null,
            el: this.name_input_container_ref,
        });
        if (this.name_input_ref) {
            this.name_input_ref.focus();
        }
    };

    phoneNumberRequiredAlert = () => {
        const { t } = this.props;
        UI.alert({
            id: 'phone-required',
            title: t('register_enter_phone_title'),
            body: t('register_enter_phone_body'),
            temp: true,
            primary_button: null,
            el: this.phone_number_input_container_ref,
        });
        if (this.phone_number_input_ref) {
            this.phone_number_input_ref.focus();
        }
    };

    phoneNumberInvalidAlert = () => {
        const { t } = this.props;
        UI.alert({
            id: 'phone-invalid',
            title: t('register_invalid_phone_title'),
            body: t('register_invalid_phone_body'),
            temp: true,
            primary_button: null,
            el: this.phone_number_input_container_ref,
        });
        if (this.phone_number_input_ref) {
            this.phone_number_input_ref.focus();
        }
    };

    passwordRequiredAlert = () => {
        const { t } = this.props;
        UI.alert({
            id: 'password-required',
            title: t('register_enter_password_title'),
            body: t('register_enter_password_body'),
            temp: true,
            primary_button: null,
            el: this.password_input_container_ref,
        });
        if (this.password_input_ref) {
            this.password_input_ref.focus();
        }
    };

    passwordInvalidAlert = () => {
        const { t } = this.props;
        UI.alert({
            id: 'password-invalid',
            title: t('register_invalid_password_title'),
            body: t('register_invalid_password_body'),
            temp: true,
            primary_button: null,
            el: this.password_input_container_ref,
        });
        if (this.password_input_ref) {
            this.password_input_ref.focus();
        }
    };

    termsAndConditionsAlert = () => {
        const { t } = this.props;
        UI.alert({
            id: 'terms-conditions-agreement-required',
            title: t('register_terms_and_conditions_title'),
            body: t('register_terms_and_conditions_body'),
            temp: true,
            primary_button: t('register_terms_and_conditions_primary_button'),
            onPrimaryButtonClick: () => {
                const { onTermsAndConditionsChange } = this.props;
                onTermsAndConditionsChange(true);
            },
            el: this.terms_and_conditions_input_ref,
        });
    };

    onNameInputKeyUp = e =>
        e.key === 'Enter' &&
        this.phone_number_input_ref &&
        this.phone_number_input_ref.focus();

    onPhoneNumberInputKeyUp = e =>
        e.key === 'Enter' &&
        this.password_input_ref &&
        this.password_input_ref.focus();

    onPasswordInputKeyUp = e => e.key === 'Enter' && this.signUp();

    focusOnNameInput() {
        if (this.name_input_ref) {
            this.name_input_ref.focus();
        }
    }

    verifyPhoneNumber = onComplete => {
        const modal = UI.ExternalModal.create(
            'customer-verification',
            <CustomerVerificationModal
                onComplete={() => {
                    modal.setIsClosable(true);
                    modal.close();
                    onComplete();
                }}
                onCancel={() => modal.close()}
                ref={ref => {
                    this.customerVerificationModalRef = ref;
                }}
            />,
            {
                'data-view': 'customer-verification',
                className: 'm2g-customer-verification',
            }
        );

        modal.setIsClosable(false);
        modal.mount();
        modal.open();
        this.customerVerificationModalRef.requestCode();
    };

    render() {

        const {
            t,
            email,
            onEmailChange,
            password,
            onPasswordChange,
            name,
            onNameChange,
            phone_number,
            onPhoneNumberChange,
            phone_number_country,
            onPhoneNumberCountryChange,
            terms_and_conditions,
            onTermsAndConditionsChange,
            subscriber,
            onSubscriberChange,
            loyaltyOptInValue,
            onLoyaltyOptInChange,
            loyalty_opt_in_enabled,
        } = this.props;

        return (
            <div>
                <EmailInput value={email} onChange={onEmailChange} />
                <NameInput
                    value={name}
                    onChange={onNameChange}
                    onKeyUp={this.onNameInputKeyUp}
                    containerRef={ref => {
                        this.name_input_container_ref = ref;
                    }}
                    ref={ref => {
                        this.name_input_ref = ref;
                    }}
                />
                {customer_phone_number_validation_enabled ? (
                    <PhoneNumberInputWithValidation
                        value={phone_number}
                        country={phone_number_country}
                        placeholder={t('phone_placeholder')}
                        onChange={onPhoneNumberChange}
                        onCountryChange={onPhoneNumberCountryChange}
                        onKeyUp={this.onPhoneNumberInputKeyUp}
                        containerRef={ref => {
                            this.phone_number_input_container_ref = ref;
                        }}
                        ref={ref => {
                            this.phone_number_input_ref = ref;
                        }}
                        containerClassName="m2g-customer-phone-input-container m2g-form-element--full-width"
                        className="m2g-customer-phone-input m2g-form-element--full-width"
                    />
                ) : (
                    <PhoneNumberInput
                        value={phone_number}
                        placeholder={t('phone_placeholder')}
                        onChange={onPhoneNumberChange}
                        onKeyUp={this.onPhoneNumberInputKeyUp}
                        containerRef={ref => {
                            this.phone_number_input_container_ref = ref;
                        }}
                        ref={ref => {
                            this.phone_number_input_ref = ref;
                        }}
                        containerClassName="m2g-customer-phone-input-container m2g-form-element--full-width"
                        className="m2g-customer-phone-input m2g-form-element--full-width"
                    />
                )}
                <RegisterPasswordInput
                    value={password}
                    onChange={onPasswordChange}
                    onKeyUp={this.onPasswordInputKeyUp}
                    containerRef={ref => {
                        this.password_input_container_ref = ref;
                    }}
                    ref={ref => {
                        this.password_input_ref = ref;
                    }}
                />
                <div data-register-password className="m2g-form-element-group">
                    <TermsAndConditionsInput
                        value={terms_and_conditions}
                        onChange={onTermsAndConditionsChange}
                        onClick={() =>
                            UI.trigger('showPolicy', 'termsandconditions')
                        }
                        ref={ref => {
                            this.terms_and_conditions_input_ref = ref;
                        }}
                    />
                    {loyalty_opt_in_enabled && (
                        <LoyaltyOptIn
                            value={loyaltyOptInValue}
                            onChange={onLoyaltyOptInChange}
                            onClick = {() => {
                                UI.trigger('showPolicy', 'loyalty')
                            }}
                            subscriberChange={onSubscriberChange}
                        />
                    )}
                    <SubscriberInput
                        value={subscriber}
                        onChange={onSubscriberChange}
                        loyaltyValue={loyaltyOptInValue}
                    />
                    <RegisterButton
                        onClick={this.signUp}
                        disabled={!terms_and_conditions}
                    />
                </div>
            </div>
        );
    }
}

export default forwardRefWithTranslation('customer')(SignUpForm);
